import { isEmpty } from 'lodash-es';

type StringRecord = Record<string, string>;

type MaybeStringRecord = StringRecord | undefined;

/**
 * Type representing the structure of the slice definitions.
 *
 * If T extends StringRecord, it includes a `name` property and an `actions` property with keys of T and values as strings.
 *
 * If T does not extend StringRecord (i.e., is undefined), it only includes the `name` property.
 */
type SliceDefinitions<T extends MaybeStringRecord> = T extends StringRecord
  ? { name: string; actions: { [K in keyof T]: string } }
  : { name: string };

/**
 * Creates slice definitions for a given name and actions.
 *
 * @template T - The type of the actions object, which should be a record with string keys and values.
 * @param {string} name - The name of the slice.
 * @param {T} [actions] - An optional object representing actions. Each action will be prefixed with the name.
 * @returns {SliceDefinitions<T>} - The slice definition object containing the name and processed actions.
 *
 * @example
 * // Create slice definitions with actions
 * const sliceDefinitions = createSliceDefinitions('QuoteStepperSlice', {
 *   updateStepper: 'updateStepper',
 * });
 * console.log(sliceDefinitions);
 * // Output:
 * // {
 * //   name: 'QuoteStepperSlice',
 * //   actions: {
 * //     updateStepper: 'QuoteStepperSlice/updateStepper'
 * //   }
 * // }
 *
 * @example
 * // Create slice definitions without actions
 * const sliceDefinitions = createSliceDefinitions('QuoteStepperSlice');
 * console.log(sliceDefinitions);
 * // Output:
 * // {
 * //   name: 'QuoteStepperSlice'
 * // }
 */
export const createSliceDefinitions = <T extends MaybeStringRecord>(
  name: string,
  actions?: T,
): SliceDefinitions<T> => {
  /**
   * Process actions by prefixing each value with the `name`,
   * if actions are provided and not empty
   */
  const processedActions = !isEmpty(actions)
    ? Object.entries(actions).reduce(
        (acc, [key, value]) => {
          acc[key as keyof T] = `${name}/${value}`;

          return acc;
        },
        {} as { [K in keyof T]: string },
      )
    : undefined;

  /**
   * Return the slice definitions,
   * conditionally including the actions if they are processed
   */
  return {
    name,
    ...(processedActions && { actions: processedActions }),
  } as SliceDefinitions<T>;
};
